/**
 * Messaging Templates panel — SMS / WhatsApp copy editor + Compliance auto-SMS opt-in.
 *
 * Lives inside `Integrations.jsx` under the SMS gateway tab.
 * Reads/writes the CMS `landing` document (content.messaging_templates), so
 * the same source of truth used by the backend `render_template()` helper.
 *
 * Each template is a self-collapsing card. Values persist in localStorage so
 * accidental page navigation doesn't force the user to re-open everything.
 */
import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Save, ChevronDown, MessageSquare, ShieldCheck, ChevronsDownUp, ChevronsUpDown,
} from "lucide-react";

const TEMPLATES = [
  { key: "staff_otp_sms",         label: "Staff sign-in OTP (SMS)",       channel: "SMS",      vars: ["otp"],                                                          sample: { otp: "489321" } },
  { key: "staff_otp_wa",          label: "Staff sign-in OTP (WhatsApp)",  channel: "WhatsApp", vars: ["otp"],                                                          sample: { otp: "489321" } },
  { key: "register_otp_sms",      label: "Customer registration OTP (SMS)", channel: "SMS",    vars: ["otp"],                                                          sample: { otp: "489321" } },
  { key: "register_otp_wa",       label: "Customer registration OTP (WhatsApp)", channel: "WhatsApp", vars: ["otp"],                                                    sample: { otp: "489321" } },
  { key: "password_reset_sms",    label: "Password reset OTP (SMS)",       channel: "SMS",     vars: ["otp"],                                                          sample: { otp: "489321" } },
  { key: "password_reset_wa",     label: "Password reset OTP (WhatsApp)",  channel: "WhatsApp", vars: ["otp"],                                                          sample: { otp: "489321" } },
  { key: "delivery_otp_sms",      label: "Delivery END OTP (SMS)",         channel: "SMS",     vars: ["otp", "order_code", "customer_name"],                            sample: { otp: "731284", order_code: "ORD-000123", customer_name: "Acme Corp" } },
  { key: "delivery_start_otp_sms", label: "Delivery START OTP (SMS)",      channel: "SMS",     vars: ["otp", "order_code"],                                              sample: { otp: "731284", order_code: "ORD-000123" } },
  { key: "delivery_start_otp_wa", label: "Delivery START OTP (WhatsApp)",  channel: "WhatsApp", vars: ["otp", "order_code", "customer_name"],                            sample: { otp: "731284", order_code: "ORD-000123", customer_name: "Acme Corp" } },
  { key: "delivery_otp_wa",       label: "Delivery END OTP (WhatsApp)",   channel: "WhatsApp", vars: ["otp", "order_code", "customer_name"],                            sample: { otp: "731284", order_code: "ORD-000123", customer_name: "Acme Corp" } },
  { key: "invoice_wa",            label: "Invoice PDF (WhatsApp)",        channel: "WhatsApp", vars: ["customer_name", "invoice_code", "total", "outstanding_line", "link"], sample: { customer_name: "Acme Corp", invoice_code: "INV-2026-014", total: "26,540.00", outstanding_line: "Outstanding: ₹26,540.00\n", link: "https://…/pdf" } },
  { key: "order_dispatched_sms",  label: "Order dispatched (SMS)",        channel: "SMS",      vars: ["order_code", "vehicle", "driver_name", "track_link", "customer_name"], sample: { order_code: "ORD-000123", vehicle: "BOW-04 MH14-AB-9911", driver_name: "Vinod Yadav", track_link: "/portal/track/xyz", customer_name: "Acme Corp" } },
  { key: "order_dispatched_wa",   label: "Order dispatched (WhatsApp)",   channel: "WhatsApp", vars: ["order_code", "driver_name", "vehicle", "track_link", "customer_name"], sample: { order_code: "ORD-000123", driver_name: "Vinod Yadav", vehicle: "BOW-04 · MH14-AB-9911", track_link: "/portal/track/xyz", customer_name: "Acme Corp" } },
  { key: "compliance_expiry_sms", label: "Compliance expiry alert (SMS)", channel: "SMS",      vars: ["item", "expiry_date", "days_left"],                              sample: { item: "Bowser BOW-04 Insurance", expiry_date: "2026-03-15", days_left: "14" } },
  { key: "compliance_expiry_wa",  label: "Compliance expiry alert (WhatsApp)", channel: "WhatsApp", vars: ["item", "expiry_date", "days_left"],                          sample: { item: "Bowser BOW-04 Insurance", expiry_date: "2026-03-15", days_left: "14" } },
];

const CHANNEL_STYLES = {
  SMS:      "bg-sky-100 text-sky-900 dark:bg-sky-500/20 dark:text-sky-200",
  WhatsApp: "bg-emerald-100 text-emerald-900 dark:bg-emerald-500/20 dark:text-emerald-200",
};

const Code = ({ children }) => (
  <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px]">{children}</code>
);

export default function MessagingTemplates({ channel = "SMS", showComplianceOptIn = false, title = "Message templates" }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    // Templates are back-office config and are no longer served by the
    // public `/cms/landing` feed (SEC-002). Read the admin-only full doc.
    queryKey: ["cms-landing-full"],
    queryFn: () => api.get("/cms/landing/full").then((r) => r.data),
  });

  // Local draft — only saved on Save
  const [draft, setDraft] = useState(null);
  const [alerts, setAlerts] = useState({ sms_enabled: false, recipient_phones: "", days_before: "30,14,7,1" });

  // Only render templates matching the current tab's channel
  const visibleTemplates = useMemo(
    () => TEMPLATES.filter((t) => t.channel === channel),
    [channel]
  );

  useEffect(() => {
    const content = data?.content;
    if (!content) return;
    setDraft(content.messaging_templates || {});
    const a = content.messaging_templates?.compliance_alerts || {};
    setAlerts({
      sms_enabled: !!a.sms_enabled,
      recipient_phones: Array.isArray(a.recipient_phones) ? a.recipient_phones.join(", ") : (a.recipient_phones || ""),
      days_before: Array.isArray(a.days_before) ? a.days_before.join(",") : (a.days_before || "30,14,7,1"),
    });
  }, [data]);

  // Per-template collapse state (persisted)
  const [collapsed, setCollapsed] = useState(() => {
    try { return JSON.parse(localStorage.getItem("msg-tmpl-collapsed") || "{}"); }
    catch { return {}; }
  });
  useEffect(() => {
    try { localStorage.setItem("msg-tmpl-collapsed", JSON.stringify(collapsed)); }
    catch { /* ignore */ }
  }, [collapsed]);
  const toggle = (k) => setCollapsed((c) => ({ ...c, [k]: !c[k] }));
  const collapseAll = () => {
    const keys = visibleTemplates.map((t) => [t.key, true]);
    if (showComplianceOptIn) keys.push(["compliance_alerts", true]);
    setCollapsed(Object.fromEntries(keys));
  };
  const expandAll = () => setCollapsed({});

  const brand = data?.content?.brand?.name || "FuelExpress";

  const save = useMutation({
    mutationFn: () => {
      const nextTemplates = { ...(draft || {}) };
      // Persist compliance opt-in nested under messaging_templates.compliance_alerts
      nextTemplates.compliance_alerts = {
        sms_enabled: !!alerts.sms_enabled,
        recipient_phones: alerts.recipient_phones.split(",").map((s) => s.trim()).filter(Boolean),
        days_before: alerts.days_before.split(",").map((s) => parseInt(s.trim(), 10)).filter((n) => Number.isFinite(n) && n >= 0),
      };
      return api.patch("/cms/landing", { content: { messaging_templates: nextTemplates } });
    },
    onSuccess: () => {
      toast.success("Message templates saved");
      qc.invalidateQueries({ queryKey: ["cms-landing-full"] });
      qc.invalidateQueries({ queryKey: ["cms-landing"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Save failed"),
  });

  const isDirty = useMemo(() => {
    if (!data?.content || !draft) return false;
    const server = data.content.messaging_templates || {};
    const draftAlerts = {
      sms_enabled: !!alerts.sms_enabled,
      recipient_phones: alerts.recipient_phones.split(",").map((s) => s.trim()).filter(Boolean),
      days_before: alerts.days_before.split(",").map((s) => parseInt(s.trim(), 10)).filter((n) => Number.isFinite(n)),
    };
    return JSON.stringify({ ...draft, compliance_alerts: draftAlerts }) !== JSON.stringify(server);
  }, [data, draft, alerts]);

  const renderPreview = (tmpl, sample) => {
    let s = tmpl || "";
    Object.entries({ brand, ...sample }).forEach(([k, v]) => { s = s.split(`{${k}}`).join(v); });
    return s.replace(/\{[a-z_]+\}/gi, "");
  };

  if (isLoading || !draft) return <div className="p-6 text-sm text-muted-foreground">Loading message templates…</div>;

  return (
    <Card data-testid="messaging-templates-card">
      <CardHeader className="flex-row items-start justify-between gap-3">
        <div>
          <CardTitle className="flex items-center gap-2"><MessageSquare size={18} /> {title}</CardTitle>
          <div className="mt-1 text-xs text-muted-foreground">
            Copy for outbound {channel} messages. Placeholders in <Code>{"{curly}"}</Code> braces are auto-filled at send-time.
            <Code>{"{brand}"}</Code> is always available (from Brand → Company name = <b>{brand}</b>).
          </div>
        </div>
        <div className="flex flex-shrink-0 flex-wrap items-center gap-1">
          <Button variant="ghost" size="sm" onClick={collapseAll} data-testid="msg-collapse-all"><ChevronsDownUp size={14} className="mr-1" /> Collapse all</Button>
          <Button variant="ghost" size="sm" onClick={expandAll} data-testid="msg-expand-all"><ChevronsUpDown size={14} className="mr-1" /> Expand all</Button>
          <Button
            size="sm"
            onClick={() => save.mutate()}
            disabled={save.isPending || !isDirty}
            data-testid="msg-templates-save"
          >
            <Save size={14} className="mr-1" /> {save.isPending ? "Saving…" : isDirty ? "Save templates" : "All saved"}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {visibleTemplates.map((t) => {
          const val = draft[t.key] || "";
          const isCollapsed = !!collapsed[t.key];
          const preview = renderPreview(val, t.sample);
          return (
            <div key={t.key} className="rounded-lg border border-border/60 bg-card" data-testid={`msg-block-${t.key}`}>
              <button
                type="button"
                onClick={() => toggle(t.key)}
                className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-muted/40"
                aria-expanded={!isCollapsed}
                data-testid={`msg-toggle-${t.key}`}
              >
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${CHANNEL_STYLES[t.channel]}`}>
                  {t.channel}
                </span>
                <span className="min-w-0 flex-1 truncate text-sm font-semibold">{t.label}</span>
                <ChevronDown size={16} className={`shrink-0 text-muted-foreground transition-transform ${isCollapsed ? "-rotate-90" : "rotate-0"}`} />
              </button>
              {!isCollapsed && (
                <div className="border-t border-border/60 p-4">
                  <div className="mb-2 flex flex-wrap items-center gap-1.5" data-testid={`msg-vars-${t.key}`}>
                    <span className="mr-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                      Available variables:
                    </span>
                    {["brand", ...t.vars].map((v) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setDraft({ ...draft, [t.key]: (draft[t.key] || "") + `{${v}}` })}
                        className="rounded-md border border-primary/30 bg-primary/5 px-1.5 py-0.5 font-mono text-[10px] text-primary transition-colors hover:bg-primary/10"
                        title={`Insert {${v}} — click to append`}
                        data-testid={`msg-var-${t.key}-${v}`}
                      >
                        {"{" + v + "}"}
                      </button>
                    ))}
                  </div>
                  <Textarea
                    rows={4}
                    value={val}
                    onChange={(e) => setDraft({ ...draft, [t.key]: e.target.value })}
                    className="font-mono text-xs"
                    placeholder="Type your message copy here — use {placeholder} braces."
                    data-testid={`msg-template-${t.key}`}
                  />
                  <div className="mt-2 rounded-md border border-dashed border-primary/40 bg-primary/5 p-3">
                    <div className="mb-1 text-[10px] font-bold uppercase tracking-widest text-primary">Live preview</div>
                    <pre className="whitespace-pre-wrap break-words font-sans text-xs leading-relaxed text-foreground" data-testid={`msg-preview-${t.key}`}>{preview || <span className="italic text-muted-foreground">(empty — falls back to system default at send-time)</span>}</pre>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* ═════════ Compliance auto-SMS opt-in (only on SMS tab) ═════════ */}
        {showComplianceOptIn && (
        <div className="rounded-lg border border-border/60 bg-card" data-testid="msg-block-compliance_alerts">
          <button
            type="button"
            onClick={() => toggle("compliance_alerts")}
            className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-muted/40"
            aria-expanded={!collapsed.compliance_alerts}
            data-testid="msg-toggle-compliance_alerts"
          >
            <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-900 dark:bg-amber-500/20 dark:text-amber-200">
              Auto-send
            </span>
            <span className="min-w-0 flex-1 truncate text-sm font-semibold flex items-center gap-2">
              <ShieldCheck size={14} /> Compliance expiry auto-SMS
            </span>
            <span className="hidden text-[10px] text-muted-foreground sm:inline">
              Opt-in — off by default
            </span>
            <ChevronDown size={16} className={`shrink-0 text-muted-foreground transition-transform ${collapsed.compliance_alerts ? "-rotate-90" : "rotate-0"}`} />
          </button>
          {!collapsed.compliance_alerts && (
            <div className="space-y-3 border-t border-border/60 p-4">
              <div className="text-xs text-muted-foreground">
                When enabled, the daily compliance scan will also send an SMS (via the <b>SMS gateway configured above</b>) using the <Code>compliance_expiry_sms</Code> template — for every bowser, driver licence, and manual compliance record that crosses one of the configured day-buckets. In-app admin notifications remain unaffected.
              </div>
              <div className="flex items-start gap-3 rounded-md border border-border/60 bg-muted/30 p-3">
                <Switch
                  checked={!!alerts.sms_enabled}
                  onCheckedChange={(v) => setAlerts({ ...alerts, sms_enabled: !!v })}
                  data-testid="compliance-sms-enabled"
                />
                <div className="flex-1">
                  <div className="text-sm font-semibold">Enable compliance expiry auto-SMS</div>
                  <div className="text-[11px] text-muted-foreground">SMS integration must be enabled and configured. To avoid alert-spam, we deduplicate against previously-notified day-buckets (30 / 14 / 7 / 1 / expired).</div>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <Label>Recipient phone numbers (comma-separated)</Label>
                  <Input
                    value={alerts.recipient_phones}
                    onChange={(e) => setAlerts({ ...alerts, recipient_phones: e.target.value })}
                    placeholder="9876543210, +919812345678"
                    className="font-mono text-xs"
                    data-testid="compliance-sms-recipients"
                    disabled={!alerts.sms_enabled}
                  />
                  <div className="mt-1 text-[10px] text-muted-foreground">Indian 10-digit or E.164 international. Empty → no SMS sent.</div>
                </div>
                <div>
                  <Label>Alert day-buckets (days before expiry)</Label>
                  <Input
                    value={alerts.days_before}
                    onChange={(e) => setAlerts({ ...alerts, days_before: e.target.value })}
                    placeholder="30,14,7,1"
                    className="font-mono text-xs"
                    data-testid="compliance-sms-days"
                    disabled={!alerts.sms_enabled}
                  />
                  <div className="mt-1 text-[10px] text-muted-foreground">Comma list. One SMS per item per bucket crossed (deduplicated for 24h).</div>
                </div>
              </div>
            </div>
          )}
        </div>
        )}
      </CardContent>
    </Card>
  );
}
